// Add and edit, for any of the fourteen tables.
//
// ONE FORM, GENERATED FROM `schema.ts`, for the same reason the API generates
// its endpoints from one registry: fourteen hand-written forms are the shape of
// code whose defects are per-table and invisible to review -- a missing
// `required`, a length that lets a person type more than the column holds.
//
// Validation is the API's and this reports it. A field's `maxLength` and
// `required` are on the input so the browser catches the ordinary mistakes, but
// the answer to "is this value acceptable" comes from the 422, whose `details`
// name the field -- and those are rendered beside their inputs.

import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as api from '../api';
import { ApiError } from '../api';
import { COLLECTIONS, REFERENCES } from '../schema';
import {
  OFFERED,
  createBody,
  duplicateOf,
  editableFields,
  formLayout,
  laidOutFields,
  newRow,
  offeredOptions,
  personIdColumn,
  referenceColumns,
  toBody,
} from './fields';
import { PeoplePicker } from './PeoplePicker';
import { useAsync, useLookups, useMe } from '../data';
import { dateInputValue, enteredBy } from '../format';
import { ErrorNote } from '../shell/Screen';
import type { Row } from '../types';

export function FormScreen({ mode }: { mode: 'create' | 'edit' }) {
  const { slug = '', id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const collection = COLLECTIONS[slug];
  const duplicateFrom = params.get('duplicate');

  const fields = useMemo(() => (collection ? editableFields(slug) : []), [collection, slug]);
  const layout = formLayout(slug);
  const placed = useMemo(() => (layout ? laidOutFields(layout) : new Set<string>()), [layout]);
  const lookups = useLookups(collection ? referenceColumns(slug) : []);

  // The keys of the part of each list this form offers, where it offers only
  // part -- see `OFFERED`. Fetched through the API's own filter rather than by
  // widening `useLookups`, whose maps the grids use to name every row, closed
  // jobs included.
  const offered = useAsync<Record<string, Set<number>>>(async () => {
    const keys: Record<string, Set<number>> = {};
    for (const [column, filter] of Object.entries(OFFERED[slug] ?? {})) {
      const reference = REFERENCES[column];
      const pk = COLLECTIONS[reference.collection].pk;
      const rows = await api.allRows(reference.collection, filter);
      keys[column] = new Set(rows.map((r) => r[pk]).filter((k): k is number => typeof k === 'number'));
    }
    return keys;
  }, [slug]);

  // Editing loads the row; duplicating loads the row it copies; creating loads
  // nothing and starts from the defaults.
  const source = useAsync<Row | null>(async () => {
    if (mode === 'edit' && id) return api.getRow(slug, id);
    if (duplicateFrom) return api.getRow(slug, duplicateFrom);
    return null;
  }, [slug, id, mode, duplicateFrom]);

  // Only the people an Admin has flagged may change Hot; the API refuses anyone
  // else. False until `/api/me` answers, and if it fails.
  const me = useMe();
  const canSetHot = Boolean(me.data?.can_set_hot);

  const [row, setRow] = useState<Row | null>(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<Error | null>(null);

  useEffect(() => {
    if (!collection) return;
    if (source.loading) return;
    // A duplicate copies Hot only for somebody who may set it, so it waits to
    // know who that is rather than dropping Hot for somebody who may.
    if (mode !== 'edit' && source.data && me.loading) return;
    if (mode === 'edit') setRow(source.data ? { ...source.data } : null);
    else setRow(source.data ? duplicateOf(slug, source.data, canSetHot) : newRow(slug));
  }, [collection, slug, mode, source.loading, source.data, me.loading, canSetHot]);

  if (!collection) return <ErrorNote error={new Error(`There is no collection named ${slug}.`)} />;
  if (source.error) return <ErrorNote error={source.error} />;
  if (!row) return <p className="muted">Loading…</p>;

  const details = failure instanceof ApiError ? failure.details : null;

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setFailure(null);
    try {
      const saved =
        mode === 'edit' && id
          ? await api.updateRow(slug, id, toBody(slug, row!, source.data))
          : await api.createRow(slug, createBody(slug, row!));
      // Back to the screen the form was opened from -- a dashboard, a grid, the
      // record -- which is where the person was working, and where Cancel
      // already goes. Each of those fetches on mount, so it shows the save.
      //
      // A form opened from a direct link has nowhere in the application to go
      // back to: react-router keys the first location of a session `default`,
      // and going back from it would leave the application. That case goes to
      // the record it saved, as every save used to.
      if (location.key !== 'default') navigate(-1);
      else navigate(`/data/${slug}/${saved[collection.pk]}`);
    } catch (caught) {
      setFailure(caught as Error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="screen">
      <header className="screen-head">
        <div>
          <h1>
            {mode === 'edit' ? 'Edit' : duplicateFrom ? 'Duplicate' : 'New'} {singular(collection.label)}
          </h1>
          {duplicateFrom && (
            <p className="screen-description">
              Pre-populated from {singular(collection.label).toLowerCase()} {duplicateFrom}. It opens today and open.
            </p>
          )}
          {/* Who raised the barrier, as text rather than an input: the API sets
              it from the token on insert and refuses it from a client. Three of
              the six barrier grids open this form on a row tap rather than the
              record, so for many people this is where they would look for it. */}
          {mode === 'edit' && slug === 'barriers' && source.data && enteredBy(source.data) && (
            <p className="screen-description">Entered by {enteredBy(source.data)}</p>
          )}
        </div>
      </header>

      {failure && <ErrorNote error={failure} />}

      {/* A layout reaches the stylesheet as custom properties rather than as
          the grid properties themselves, so that a narrow screen can set them
          aside and stack the fields in their order. */}
      <form
        className={layout ? 'form laid-out' : 'form'}
        style={
          layout
            ? ({
                '--form-columns': layout.columns,
                '--form-rows': layout.rows.map((r) => `"${r}"`).join(' '),
              } as CSSProperties)
            : undefined
        }
        onSubmit={save}
      >
        {fields.map((field) => {
          const reference = REFERENCES[field.name];
          const lookup = lookups.data?.[field.name];
          const value = row![field.name];
          const detail = details?.[field.name];
          const set = (next: unknown) => setRow({ ...row!, [field.name]: next });
          const area = placed.has(field.name) ? ({ '--form-area': field.name } as CSSProperties) : undefined;

          // A person from the directory: the name is shown and the object id is
          // what is sent. Not a `<label>`, because it holds a button and a list
          // as well as its input, and a label passes a click to its first control.
          const idColumn = personIdColumn(slug, field.name);
          if (idColumn) {
            const problem = detail ?? details?.[idColumn];
            return (
              <div key={field.name} className={problem ? 'field failed' : 'field'} style={area}>
                <span className="field-label" id={`field-${field.name}`}>
                  {field.label}
                </span>
                <PeoplePicker
                  labelledBy={`field-${field.name}`}
                  invalid={Boolean(problem)}
                  value={{
                    objectId: (row![idColumn] as string | null | undefined) ?? null,
                    name: (value as string | null | undefined) ?? null,
                  }}
                  onChange={(person) => setRow({ ...row!, [field.name]: person.name, [idColumn]: person.objectId })}
                />
                {problem && <span className="field-error">{problem}</span>}
              </div>
            );
          }

          return (
            <label
              key={field.name}
              className={detail ? 'field failed' : 'field'}
              style={area}
            >
              <span className="field-label">
                {field.label}
                {field.required && <em aria-hidden="true"> *</em>}
              </span>

              {field.type === 'bool' && hotLocked(slug, field.name, canSetHot) ? (
                // Shown, so everyone sees whether the barrier is hot, and not
                // changeable. The edit still sends it back unchanged, which the
                // API takes as no change.
                <>
                  <input type="checkbox" checked={Boolean(value)} disabled />
                  <span className="field-hint">Set by designated people only</span>
                </>
              ) : field.type === 'bool' ? (
                <input type="checkbox" checked={Boolean(value)} onChange={(e) => set(e.target.checked)} />
              ) : reference ? (
                <select value={value === null || value === undefined ? '' : String(value)} onChange={(e) => set(e.target.value === '' ? null : Number(e.target.value))}>
                  <option value="">(none)</option>
                  {offeredOptions(
                    lookup,
                    OFFERED[slug]?.[field.name] ? (offered.data?.[field.name] ?? new Set()) : null,
                    source.data?.[field.name]
                  ).map(([key, text]) => (
                    <option key={key} value={key}>
                      {text}
                    </option>
                  ))}
                </select>
              ) : field.choices ? (
                <select value={value === null || value === undefined ? '' : String(value)} onChange={(e) => set(e.target.value === '' ? null : e.target.value)}>
                  <option value="">(none)</option>
                  {field.choices.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice}
                    </option>
                  ))}
                </select>
              ) : field.type === 'date' ? (
                <input
                  type="date"
                  value={dateInputValue(value)}
                  required={field.required}
                  onChange={(e) => set(e.target.value || null)}
                />
              ) : field.type === 'int' ? (
                <input
                  type="number"
                  value={value === null || value === undefined ? '' : String(value)}
                  required={field.required}
                  onChange={(e) => set(e.target.value === '' ? null : Number(e.target.value))}
                />
              ) : (field.maxLength ?? 0) > 200 ? (
                <textarea
                  value={value === null || value === undefined ? '' : String(value)}
                  maxLength={field.maxLength ?? undefined}
                  required={field.required}
                  rows={4}
                  onChange={(e) => set(e.target.value)}
                />
              ) : (
                <input
                  type="text"
                  value={value === null || value === undefined ? '' : String(value)}
                  maxLength={field.maxLength ?? undefined}
                  required={field.required}
                  onChange={(e) => set(e.target.value)}
                />
              )}

              {detail && <span className="field-error">{detail}</span>}
            </label>
          );
        })}

        <div className="form-actions">
          <button type="submit" className="primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="link" onClick={() => navigate(-1)}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}

/** Whether a field is a barrier's Hot that this person may not change. */
function hotLocked(slug: string, name: string, canSetHot: boolean): boolean {
  return slug === 'barriers' && name === 'is_hot' && !canSetHot;
}

function singular(label: string): string {
  if (label.endsWith('ies')) return `${label.slice(0, -3)}y`;
  if (label.endsWith('es') && !label.endsWith('ses')) return label.slice(0, -2);
  if (label.endsWith('s')) return label.slice(0, -1);
  return label;
}
